import { useCallback, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import { createApiResponse, createErrorApiResponse } from "@entrilia/shared/api/axiosHelper";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import type { RefreshDocumentUrl } from "../services/pdfPreview/pdfLoadErrorHandler";
import type { ResolveFailureReason, ResolvedFileUrl } from "../services/viewOnlyFileProtection/resolveProtectedFileUrl";
import {
  mustResolveUrl,
  resolveProtectedFileUrl,
  selectEffectiveUrl,
} from "../services/viewOnlyFileProtection/resolveProtectedFileUrl";
import { refreshViewOnlyFileProtection } from "../store/state/user/thunks";
import type { AppDispatch, RootState } from "../store/store";

const decryptErrorMessage = "Unable to decrypt view-only file URL";

interface ViewOnlyFileUrl {
  effectiveUrl: string | undefined;
  decryptFailed: boolean;
  canRetry: boolean;
  isRetrying: boolean;
  retryDecrypt: () => Promise<void>;
  refreshEffectiveUrl: RefreshDocumentUrl | undefined;
}

export const useViewOnlyFileUrl = (url: string, refreshUrl?: RefreshDocumentUrl): ViewOnlyFileUrl => {
  const dispatch: AppDispatch = useDispatch();
  const store = useStore<RootState>();
  const [failureReason, setFailureReason] = useState<ResolveFailureReason>();

  const resolveUrl = useCallback(
    async (targetUrl: string): Promise<ResolvedFileUrl> => {
      const resolved = await resolveProtectedFileUrl(targetUrl, {
        // Read at call time, so the fetcher keeps its identity and useFetch does not re-run on every refresh.
        protection: store.getState().user.viewOnlyFileProtection,
        refreshProtection: () => dispatch(refreshViewOnlyFileProtection()),
      });

      // Shared by the initial resolve and the SAS-refresh path, so both surface the same dialog.
      setFailureReason(resolved.status === "error" ? resolved.reason : undefined);

      if (resolved.status === "error") {
        logError(
          new Error(`${decryptErrorMessage}: ${resolved.reason}`),
          "[useViewOnlyFileUrl] resolveProtectedFileUrl"
        );
      }

      return resolved;
    },
    [dispatch, store]
  );

  const resolveEffectiveUrl = useCallback(async () => {
    const resolved = await resolveUrl(url);
    return resolved.status === "resolved"
      ? createApiResponse(resolved.url)
      : createErrorApiResponse<string>({ message: decryptErrorMessage });
  }, [resolveUrl, url]);

  const refreshEffectiveUrl = useCallback(async () => {
    const fresh = await refreshUrl?.();
    if (!fresh) {
      return undefined;
    }

    const resolved = await resolveUrl(fresh.downloadUrl);
    // Undefined routes the failure to the retry dialog, not the blob diagnostics one.
    return resolved.status === "resolved" ? { ...fresh, downloadUrl: resolved.url } : undefined;
  }, [refreshUrl, resolveUrl]);

  const mustResolve = mustResolveUrl(url);
  const [decrypted, , { isFetching, fetch }] = useFetch(resolveEffectiveUrl, { disabled: !mustResolve });

  return {
    effectiveUrl: selectEffectiveUrl(url, { mustResolve, isResolving: isFetching, resolved: decrypted }),
    decryptFailed: !!failureReason,
    // Only an unanswered probe is worth another /User/permissions call; the rest cannot change outcome.
    canRetry: mustResolve && failureReason === "unverified",
    isRetrying: isFetching,
    retryDecrypt: fetch,
    refreshEffectiveUrl: refreshUrl && refreshEffectiveUrl,
  };
};
